import React from 'react';

// Builds a compact page list with "..." collapsing, e.g.
// [1, '...', 4, 5, 6, '...', 12] instead of rendering every page.
const buildPageList = (current, total, siblingCount = 1) => {
  const pages = [];
  for (let i = 1; i <= total; i += 1) {
    if (i === 1 || i === total || (i >= current - siblingCount && i <= current + siblingCount)) {
      pages.push(i);
    }
  }
  const withDots = [];
  let previous = 0;
  pages.forEach((page) => {
    if (previous) {
      if (page - previous === 2) withDots.push(previous + 1);
      else if (page - previous !== 1) withDots.push('…');
    }
    withDots.push(page);
    previous = page;
  });
  return withDots;
};

/**
 * Pagination
 * ------------------------------------------------------------------
 * "Showing X to Y of Z {itemLabel}" summary + numbered page controls
 * with prev/next arrows. Collapses long page ranges with "…" so it
 * never overflows on smaller screens. Fully controlled.
 *
 * @param {number} currentPage - 1-indexed
 * @param {number} totalItems
 * @param {number} pageSize
 * @param {function} onPageChange - (page) => void
 * @param {string} itemLabel - e.g. "Projects"
 * @param {string} prevIconSrc / nextIconSrc
 */
const Pagination = ({
  currentPage = 1,
  totalItems = 0,
  pageSize = 10,
  onPageChange,
  itemLabel = 'items',
  prevIconSrc,
  nextIconSrc,
  className = '',
}) => {
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  if (totalItems === 0) return null;

  const start = (currentPage - 1) * pageSize + 1;
  const end = Math.min(currentPage * pageSize, totalItems);
  const pages = buildPageList(currentPage, totalPages);

  const goTo = (page) => {
    if (page < 1 || page > totalPages || page === currentPage) return;
    onPageChange?.(page);
  };

  return (
    <nav className={`pagination ${className}`} aria-label="Pagination">
      <p className="pagination__summary">
        Showing {start} to {end} of {totalItems} {itemLabel}
      </p>

      <div className="pagination__controls">
        <button
          type="button"
          className="pagination__nav"
          disabled={currentPage === 1}
          onClick={() => goTo(currentPage - 1)}
          aria-label="Previous page"
        >
          <img src={prevIconSrc} alt="" className="pagination__nav-icon pagination__nav-icon--prev" />
        </button>

        {pages.map((page, index) =>
          page === '…' ? (
            <span key={`dots-${index}`} className="pagination__dots" aria-hidden="true">
              …
            </span>
          ) : (
            <button
              key={page}
              type="button"
              className={`pagination__page ${page === currentPage ? 'pagination__page--active' : ''}`}
              aria-current={page === currentPage ? 'page' : undefined}
              onClick={() => goTo(page)}
            >
              {page}
            </button>
          )
        )}

        <button
          type="button"
          className="pagination__nav"
          disabled={currentPage === totalPages}
          onClick={() => goTo(currentPage + 1)}
          aria-label="Next page"
        >
          <img src={nextIconSrc} alt="" className="pagination__nav-icon" />
        </button>
      </div>
    </nav>
  );
};

export default Pagination;